import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Bell, Gavel, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';

const NOTIFICATION_ICONS = {
  new_bid: Gavel,
  new_proposal: FileText,
  property_approved: CheckCircle,
  default: Bell
};

const NOTIFICATION_COLORS = {
  new_bid: 'bg-[#0071e3] text-white',
  new_proposal: 'bg-[#0071e3] text-white',
  property_approved: 'bg-green-500 text-white',
  default: 'bg-blue-500 text-white'
};

const PushNotificationProvider = ({ userId, children }) => {
  const [connected, setConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!userId) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/notifications/${userId}`;

    console.log('[Push] Connecting to:', wsUrl);

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('[Push] Connected');
        setConnected(true);
        reconnectAttempts.current = 0;
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[Push] Received:', data);

          if (data.type === 'init') {
            setUnreadCount(data.unread_count || 0);
          } else if (data.type === 'push_notification') {
            // Show toast notification
            showNotificationToast(data);
            // Update unread count
            setUnreadCount(prev => prev + 1);
          }
        } catch (e) {
          console.error('[Push] Parse error:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('[Push] Disconnected', event.code);
        setConnected(false);

        // Attempt reconnection
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`[Push] Reconnecting in ${delay}ms...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('[Push] Error:', error);
      };
    } catch (error) {
      console.error('[Push] Connection error:', error);
    }
  }, [userId]);

  const showNotificationToast = (notification) => {
    const Icon = NOTIFICATION_ICONS[notification.notification_type] || NOTIFICATION_ICONS.default;
    const colorClass = NOTIFICATION_COLORS[notification.notification_type] || NOTIFICATION_COLORS.default;

    toast.custom((t) => (
      <div className={`flex items-start gap-3 p-4 rounded-lg shadow-lg ${colorClass} max-w-sm`}>
        <div className="flex-shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{notification.title}</p>
          <p className="text-xs opacity-90 mt-1 line-clamp-2">{notification.message}</p>
        </div>
        <button 
          onClick={() => toast.dismiss(t)}
          className="flex-shrink-0 opacity-70 hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    ), {
      duration: 5000,
      position: 'top-right'
    });
  };

  useEffect(() => {
    connect();

    // Setup ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  // Expose context values
  const contextValue = {
    connected,
    unreadCount,
    setUnreadCount
  };

  return (
    <PushNotificationContext.Provider value={contextValue}>
      {children}
    </PushNotificationContext.Provider>
  );
};

// Context for accessing notification state
export const PushNotificationContext = React.createContext({
  connected: false,
  unreadCount: 0,
  setUnreadCount: () => {}
});

export const usePushNotifications = () => React.useContext(PushNotificationContext);

export default PushNotificationProvider;
